import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useIdleTimerContext } from 'react-idle-timer';

import { removePersistedValue } from 'shared/utils/persisted-values';
import { clearConciergeConversationKeys } from 'src/concierge/conversation-storage';
import useUser from 'src/hooks/use-user';
import useCart from 'shared/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import { useModals } from 'src/components/modals/use-modals';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { clearAllPublicSessionSecrets } from './use-session-secret';

export type TeardownKioskSessionUser = {
  isLoggedIn?: boolean;
  logout?: () => Promise<void> | void;
  setKioskGuestPhoneNumber?: (phone: string | null) => void;
  clearLoyaltyPin?: (dispensaryId: string) => void;
  viewOrderId?: string | null;
  isPublicSessionPhoneNumberLogin?: boolean;
  activeVerificationToken?: string | null;
  hasLoyaltyAccount?: boolean;
  postSignupModalSequenceDispensaryId?: string | null;
};

export type TeardownKioskSessionCart = {
  clearOrder?: () => void;
  setAnonymousOrderId?: (id: string) => void;
  setIsAnonymousKioskCheckout?: (isAnonymous: boolean) => void;
};

export type TeardownKioskSessionUI = {
  showKioskCheckoutModal?: boolean;
  showKioskOrderSuccess?: boolean;
  setIsKioskProLoginModalOpen?: (isOpen: boolean) => void;
  updateShowDutchiePayQrCodeInKiosk?: (show: boolean) => void;
  updateShowKioskSelectPaymentModal?: (show: boolean) => void;
  updateShowDutchiePayKioskOrderSuccess?: (show: boolean) => void;
  closeCartDrawer?: () => void;
  closeCartInterstitial?: () => void;
  redirectToKioskLockScreen?: (cName?: string) => void;
};

export type TeardownKioskSessionModals = {
  activeModals?: Map<string, unknown>;
  closeModal?: (name: string, data?: unknown) => void;
};

export type TeardownKioskSessionIdleTimer = {
  reset?: () => void;
};

export type TeardownKioskSessionApolloClient = Pick<ApolloClient<unknown>, 'clearStore'>;

export type TeardownKioskSessionParams = {
  user?: TeardownKioskSessionUser | null;
  cart?: TeardownKioskSessionCart | null;
  ui?: TeardownKioskSessionUI | null;
  apolloClient?: TeardownKioskSessionApolloClient | null;
  idleTimer?: TeardownKioskSessionIdleTimer | null;
  dispensaryId?: string | null;
  modals?: TeardownKioskSessionModals | null;
  clearSessionSecrets?: boolean;
};

// One shared count of Kiosk Pro session ends. Every teardown bumps it, so work started for the
// previous shopper (e.g. a login waiting on its public session) can see that the session ended.
let kioskSessionGeneration = 0;

export function getKioskSessionGeneration(): number {
  return kioskSessionGeneration;
}

async function attemptTeardown(operation: () => Promise<void> | void, errorMessage: string): Promise<void> {
  try {
    await operation();
  } catch (error) {
    console.error(errorMessage, error);
  }
}

/**
 * Executes a single, comprehensive teardown of customer-specific state across Kiosk Pro.
 * Clears authentication, guest phone identity, persisted loyalty PINs, viewOrderId,
 * cart items, sensitive open modals, Apollo query cache, and resets the idle timer.
 */
export async function teardownKioskSession({
  user,
  cart,
  ui,
  apolloClient,
  idleTimer,
  dispensaryId,
  modals,
  clearSessionSecrets = true,
}: TeardownKioskSessionParams): Promise<void> {
  kioskSessionGeneration += 1;
  // 1. UI & Modals: close cart drawers, modal flags, active modals
  try {
    if (ui) {
      ui.showKioskCheckoutModal = false;
      ui.showKioskOrderSuccess = false;
      ui.setIsKioskProLoginModalOpen?.(false);
      ui.updateShowDutchiePayQrCodeInKiosk?.(false);
      ui.updateShowKioskSelectPaymentModal?.(false);
      ui.updateShowDutchiePayKioskOrderSuccess?.(false);
      ui.closeCartDrawer?.();
      ui.closeCartInterstitial?.();
    }

    if (modals?.activeModals && typeof modals.closeModal === 'function') {
      const { closeModal } = modals;
      const activeKeys = Array.from(modals.activeModals.keys());
      activeKeys.forEach((modalName) => {
        try {
          closeModal(modalName, undefined);
        } catch {
          // best-effort modal dismissal
        }
      });
    }
  } catch (error) {
    console.error('Error closing UI/modals during kiosk teardown:', error);
  } finally {
    // Always release body scroll locks, even if a UI/modal callback above threw.
    try {
      clearAllBodyScrollLocks();
    } catch {
      // best-effort scroll lock release
    }
  }

  // 2. Cart & Order state
  try {
    cart?.clearOrder?.();
    cart?.setAnonymousOrderId?.('');
    cart?.setIsAnonymousKioskCheckout?.(false);
  } catch (error) {
    console.error('Error clearing cart during kiosk teardown:', error);
  }

  // 3. User & Identity state + sensitive storage keys
  await attemptTeardown(
    () => user?.setKioskGuestPhoneNumber?.(null),
    'Error clearing guest phone during kiosk teardown:'
  );
  await attemptTeardown(() => {
    if (dispensaryId) {
      user?.clearLoyaltyPin?.(dispensaryId);
    }
  }, 'Error clearing dispensary loyalty PIN during kiosk teardown:');
  await attemptTeardown(
    () => removePersistedValue('loyaltyPins'),
    'Error clearing loyalty PINs during kiosk teardown:'
  );
  await attemptTeardown(() => {
    if (user) {
      user.viewOrderId = null;
    }
  }, 'Error clearing view order state during kiosk teardown:');
  await attemptTeardown(
    () => removePersistedValue('viewOrderId', true),
    'Error clearing session view order ID during kiosk teardown:'
  );
  await attemptTeardown(
    () => removePersistedValue('viewOrderId'),
    'Error clearing local view order ID during kiosk teardown:'
  );
  await attemptTeardown(
    () => removePersistedValue('verification-token'),
    'Error clearing verification token during kiosk teardown:'
  );
  await attemptTeardown(
    () => removePersistedValue('isPublicSessionPhoneNumberLogin', true),
    'Error clearing session phone-login state during kiosk teardown:'
  );
  await attemptTeardown(
    () => removePersistedValue('isPublicSessionPhoneNumberLogin'),
    'Error clearing local phone-login state during kiosk teardown:'
  );
  if (clearSessionSecrets) {
    await attemptTeardown(() => {
      clearAllPublicSessionSecrets();
    }, 'Error clearing session secrets during kiosk teardown:');
  }
  await attemptTeardown(
    () => clearConciergeConversationKeys(),
    'Error clearing Concierge storage during kiosk teardown:'
  );
  await attemptTeardown(() => {
    if (user) {
      user.isPublicSessionPhoneNumberLogin = false;
      user.activeVerificationToken = null;
      user.hasLoyaltyAccount = false;
      user.postSignupModalSequenceDispensaryId = null;
    }
  }, 'Error clearing in-memory user state during kiosk teardown:');
  if (user?.isLoggedIn) {
    await attemptTeardown(() => user.logout?.(), 'Error logging out during kiosk teardown:');
  }

  // 4. Apollo Client cache purge
  if (apolloClient?.clearStore) {
    await attemptTeardown(async () => {
      await apolloClient.clearStore();
    }, 'Error clearing apollo store during kiosk teardown:');
  }

  // 5. Idle timer reset
  await attemptTeardown(() => idleTimer?.reset?.(), 'Error resetting idle timer during kiosk teardown:');
}

/**
 * React hook providing the unified Kiosk Pro session teardown function bound to active stores.
 */
export function useKioskSessionTeardown(): () => Promise<void> {
  const User = useUser();
  const Cart = useCart();
  const UI = useUI();
  const Modals = useModals();
  const apolloClient = useApolloClient();
  const idleTimer = useIdleTimerContext();

  return useCallback(async (): Promise<void> => {
    await teardownKioskSession({
      user: User,
      cart: Cart,
      ui: UI,
      modals: Modals,
      apolloClient,
      idleTimer,
    });
  }, [User, Cart, UI, Modals, apolloClient, idleTimer]);
}
